import { X, Sparkles, Clock, CheckCheck, Edit3, XCircle } from 'lucide-react';
import { cn } from '@/utils/cn';
import { useMacHistory } from '@/hooks/useMacSuggest';
import { Spinner } from '@/components/ui';
import type { MacHistoryEvent } from '@/api/macSuggest';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  patientId: string;
}

function AuditSourceBadge({ source }: { source?: string | null }) {
  if (!source) return <span className="text-shell-muted text-xs">не отправлено</span>;
  if (source === 'ai_clean') return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700">
      <CheckCheck className="w-3 h-3" />отправлено как есть
    </span>
  );
  if (source === 'ai_edited') return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700">
      <Edit3 className="w-3 h-3" />отредактировано
    </span>
  );
  return <span className="text-shell-muted text-xs">{source}</span>;
}

function HistoryRow({ event }: { event: MacHistoryEvent }) {
  const date = new Date(event.created_at).toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
  });

  return (
    <tr className="border-b border-shell-border last:border-0 hover:bg-shell-hover/50 transition-colors">
      <td className="px-3 py-2.5 text-xs text-shell-muted whitespace-nowrap">{date}</td>
      <td className="px-3 py-2.5 text-xs text-shell-muted capitalize">{event.mode || '—'}</td>
      <td className="px-3 py-2.5 text-sm text-shell-strong max-w-[180px]">
        <span
          title={event.intent_text}
          className="line-clamp-2 block"
        >
          {event.intent_text || '—'}
        </span>
      </td>
      <td className="px-3 py-2.5 text-xs text-shell-muted text-center">{event.variants_requested}</td>
      <td className="px-3 py-2.5">
        <AuditSourceBadge source={event.final_ai_source} />
      </td>
      <td className="px-3 py-2.5 text-xs text-shell-muted text-right whitespace-nowrap">
        {event.response_latency_ms != null ? `${event.response_latency_ms} мс` : '—'}
      </td>
    </tr>
  );
}

export function CopilotHistoryModal({ isOpen, onClose, tenantId, patientId }: Props) {
  const { data, isLoading, isError } = useMacHistory(tenantId, patientId || undefined, isOpen && !!patientId);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-shell-strong/60 backdrop-blur-sm p-4">
      <div className="shell-card w-full max-w-3xl max-h-[80vh] flex flex-col animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-shell-border flex-shrink-0">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-violet-500" />
            <h3 className="font-semibold text-shell-strong">История AI-черновиков</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-shell-hover text-shell-muted transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto">
          {isLoading && (
            <div className="flex items-center justify-center py-10">
              <Spinner size="md" />
            </div>
          )}

          {isError && (
            <div className="flex flex-col items-center justify-center py-10 gap-2 text-shell-muted">
              <XCircle className="w-6 h-6 text-red-400" />
              <span className="text-sm">Не удалось загрузить историю</span>
            </div>
          )}

          {!isLoading && !isError && !data?.events.length && (
            <div className="flex flex-col items-center justify-center py-10 gap-2 text-shell-muted">
              <Sparkles className="w-6 h-6 text-violet-300" />
              <span className="text-sm">AI-черновиков по этому пациенту пока нет</span>
            </div>
          )}

          {!isLoading && !isError && !!data?.events.length && (
            <table className="w-full text-sm">
              <thead className="bg-shell-surface sticky top-0">
                <tr className="border-b border-shell-border">
                  <th className={cn(thCls, 'text-left')}>Дата</th>
                  <th className={cn(thCls, 'text-left')}>Режим</th>
                  <th className={cn(thCls, 'text-left')}>Суть</th>
                  <th className={cn(thCls, 'text-center')}>Вар.</th>
                  <th className={cn(thCls, 'text-left')}>Итог</th>
                  <th className={cn(thCls, 'text-right')}>Задержка</th>
                </tr>
              </thead>
              <tbody>
                {data.events.map(e => <HistoryRow key={e.id} event={e} />)}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

const thCls = 'px-3 py-2.5 text-xs font-semibold text-shell-muted';
